import { useEffect, useRef, useState } from 'react'
import {
  getRegistrationDynamicOptions,
  type DynamicOption,
} from '@/services/registrationApi'
import type { OptionSource } from '@/types/survey'

interface UseDynamicSurveyOptionsArgs {
  snapshotId: string | null | undefined
  questionCode: string
  optionSource: OptionSource | undefined
  /**
   * Current form answers, keyed by question code. Only the codes that
   * appear in `optionSource.depends_on` actually drive a refetch — other
   * answer changes are ignored.
   */
  currentAnswers: Record<string, unknown>
  language?: 'en' | 'ar'
  /**
   * Optional case-insensitive typeahead query. The caller (typically a
   * combobox) is responsible for debouncing — every change here triggers a
   * fetch.
   */
  search?: string
  /** Page size for the request. Defaults to the backend's 50. */
  limit?: number
}

interface UseDynamicSurveyOptionsResult {
  options: DynamicOption[]
  isLoading: boolean
  error: string | null
  /** True iff a depends_on parent answer is still missing. */
  awaitingParent: boolean
}

/**
 * Resolves model-backed survey dropdown options for a single question.
 *
 * Refetches whenever:
 *   - `snapshotId` or `questionCode` change, or
 *   - any answer named in `optionSource.depends_on[].from_question_code` changes.
 *
 * Returns `{ options: [], awaitingParent: true }` when a required parent
 * answer hasn't been picked yet — the caller should disable the control.
 */
export function useDynamicSurveyOptions({
  snapshotId,
  questionCode,
  optionSource,
  currentAnswers,
  language = 'en',
  search,
  limit,
}: UseDynamicSurveyOptionsArgs): UseDynamicSurveyOptionsResult {
  const [options, setOptions] = useState<DynamicOption[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Build the dependency-narrowed answers slice up front. We use it both as
  // the network request body and as a stable JSON key to control refetches —
  // unrelated answer changes (e.g. typing in a phone field) must not retrigger.
  const dependencyKeys = optionSource?.depends_on?.map(d => d.from_question_code) ?? []
  const dependencyAnswers: Record<string, unknown> = {}
  for (const key of dependencyKeys) {
    dependencyAnswers[key] = currentAnswers[key]
  }
  const dependencyJson = JSON.stringify(dependencyAnswers)

  const awaitingParent = dependencyKeys.some(
    k => currentAnswers[k] === undefined || currentAnswers[k] === '' || currentAnswers[k] === null,
  )

  // Track the in-flight controller so a fast cascade (city → neighborhood,
  // then user changes city again) doesn't race a stale response into state.
  const abortRef = useRef<AbortController | null>(null)

  useEffect(() => {
    if (!optionSource || !snapshotId) {
      setOptions([])
      setError(null)
      return
    }

    // Parent isn't picked yet — don't bother the network.
    if (awaitingParent) {
      setOptions([])
      setError(null)
      setIsLoading(false)
      return
    }

    abortRef.current?.abort()
    const controller = new AbortController()
    abortRef.current = controller

    setIsLoading(true)
    setError(null)

    getRegistrationDynamicOptions(
      snapshotId,
      questionCode,
      JSON.parse(dependencyJson),
      language,
      controller.signal,
      search,
      limit,
    )
      .then(opts => {
        if (controller.signal.aborted) return
        setOptions(opts)
      })
      .catch(err => {
        if (controller.signal.aborted) return
        if (err?.name === 'CanceledError' || err?.code === 'ERR_CANCELED') return
        const message = err instanceof Error ? err.message : 'Failed to load options'
        setError(message)
        setOptions([])
      })
      .finally(() => {
        if (controller.signal.aborted) return
        setIsLoading(false)
      })

    return () => controller.abort()
    // dependencyJson collapses the watched answers into a single primitive
    // so React's shallow compare correctly detects parent-answer changes.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [snapshotId, questionCode, dependencyJson, awaitingParent, language, search, limit])

  return { options, isLoading, error, awaitingParent }
}
